import { createApi } from '@reduxjs/toolkit/query/react';
import {
  PaginatedResponse,
  PaginationParams,
  SortParams,
  Technician,
  TechnicianFilters,
} from '../../types';
import { baseQueryStub, shouldRefetchOnSortChange } from '../../utils';
import { TechnicianService } from './technician-service';

// A helper function to initialize the service once when needed
let technicianService: TechnicianService | null = null;
const getTechnicianService = () => {
  technicianService ??= new TechnicianService();
  return technicianService;
};

// Define an API service using RTK Query that uses the TechnicianService
export const techniciansApi = createApi({
  reducerPath: 'techniciansApi',
  baseQuery: baseQueryStub,
  tagTypes: ['Technician'],
  serializeQueryArgs: ({ queryArgs, endpointName }) => {
    // For the getTechnicians endpoint, include sort in the cache key
    if (endpointName === 'getTechnicians') {
      const { pagination, filters, sort } = queryArgs as {
        pagination: PaginationParams;
        filters?: TechnicianFilters;
        sort?: SortParams;
      };

      // Create a cache key that includes sort parameters
      return JSON.stringify({
        endpointName,
        pagination,
        filters,
        sort,
      });
    }

    // For other endpoints, use default serialization
    return JSON.stringify({ endpointName, queryArgs });
  },
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: builder => ({
    getTechnicians: builder.query<
      PaginatedResponse<Technician>,
      {
        pagination: PaginationParams;
        filters?: TechnicianFilters;
        sort?: SortParams;
      }
    >({
      queryFn: async ({ pagination, filters, sort }) => {
        const { page, pageSize } = pagination;
        const technicianService = getTechnicianService();
        const response = await technicianService.getTechnicians(page, pageSize, filters, sort);
        return { data: response };
      },
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Technician' as const, id })),
              { type: 'Technician', id: 'LIST' },
            ]
          : [{ type: 'Technician', id: 'LIST' }],
      keepUnusedDataFor: 30, // 30 seconds
      forceRefetch: shouldRefetchOnSortChange,
    }),
    getAllTechnicians: builder.query<Technician[], void>({
      // For cases where all technicians are needed without pagination
      queryFn: async () => {
        // Get all technicians with a large page size
        const technicianService = getTechnicianService();
        const response = await technicianService.getTechnicians(1, 1000);
        return { data: response.data };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Technician' as const, id })),
              { type: 'Technician', id: 'LIST' },
            ]
          : [{ type: 'Technician', id: 'LIST' }],
      keepUnusedDataFor: 60, // 60 seconds
    }),
    getActiveTechniciansByManufacturer: builder.query<Technician[], number>({
      queryFn: async manufacturerId => {
        const technicianService = getTechnicianService();
        const technicians =
          await technicianService.getActiveTechniciansByManufacturer(manufacturerId);
        return { data: technicians };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Technician' as const, id })),
              { type: 'Technician', id: 'LIST' },
            ]
          : [{ type: 'Technician', id: 'LIST' }],
      keepUnusedDataFor: 60,
    }),
    getTechnicianById: builder.query<Technician, string>({
      queryFn: async id => {
        const technicianService = getTechnicianService();
        const technician = await technicianService.getTechnicianById(id);
        return { data: technician };
      },
      providesTags: (_, __, id) => [{ type: 'Technician', id }],
    }),
    addTechnician: builder.mutation<Technician, { technicianData: Omit<Technician, 'id'> }>({
      queryFn: async ({ technicianData }) => {
        const technicianService = getTechnicianService();
        const newTechnician = await technicianService.createTechnician(technicianData);
        return { data: newTechnician };
      },
      invalidatesTags: ['Technician'],
    }),
    updateTechnician: builder.mutation<void, Partial<Technician> & { id: string }>({
      queryFn: async technician => {
        const { id, ...data } = technician;
        const technicianService = getTechnicianService();
        await technicianService.updateTechnician(id, data);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'Technician', id },
        { type: 'Technician', id: 'LIST' },
      ],
    }),
    deleteTechnician: builder.mutation<void, string>({
      queryFn: async id => {
        const technicianService = getTechnicianService();
        await technicianService.deleteTechnician(id);
        return { data: undefined };
      },
      invalidatesTags: (_, __, id) => [
        { type: 'Technician', id },
        { type: 'Technician', id: 'LIST' },
      ],
    }),
    validateInitialsUniqueness: builder.query<
      { isUnique: boolean },
      { technicianId: number | null; initials: string; manufacturerId: number }
    >({
      queryFn: async ({ technicianId, initials, manufacturerId }) => {
        const technicianService = getTechnicianService();
        const isUnique = await technicianService.validateInitialsUniqueness(
          technicianId,
          initials,
          manufacturerId
        );
        return { data: { isUnique } };
      },
      // Don't cache validation results - always fetch fresh data
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetTechniciansQuery,
  useGetAllTechniciansQuery,
  useGetActiveTechniciansByManufacturerQuery,
  useGetTechnicianByIdQuery,
  useAddTechnicianMutation,
  useUpdateTechnicianMutation,
  useDeleteTechnicianMutation,
  useValidateInitialsUniquenessQuery,
  usePrefetch,
} = techniciansApi;
